import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Plus, Trash2 } from "lucide-react";

import ReportDefinitionEditor, { blankDefinition, CHART_LABELS } from "./ReportDefinitionEditor";
import {
  deleteReportDefinition,
  fetchReportBuilderMeta,
  fetchReportDefinitions,
} from "../lib/api";

/**
 * The report builder: a list of definitions beside the editor.
 *
 * The editor itself is shared with the board pack, so a report can equally be
 * tweaked where it is being read. Both edit the same stored definition.
 */
export default function ReportBuilderPanel({ portalId }) {
  const [meta, setMeta] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  const reloadDefinitions = useCallback(async () => {
    if (!portalId) return;
    try {
      const data = await fetchReportDefinitions(portalId);
      setDefinitions(data.definitions || []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load report definitions.");
    }
  }, [portalId]);

  useEffect(() => {
    fetchReportBuilderMeta().then(setMeta).catch(() => setMeta(null));
  }, []);

  useEffect(() => {
    reloadDefinitions();
  }, [reloadDefinitions]);

  const handleDelete = async (key) => {
    await deleteReportDefinition(portalId, key);
    if (draft?.key === key) setDraft(null);
    await reloadDefinitions();
  };

  const sources = meta?.sources || [{ value: "deals", label: "Deal" }];

  if (!portalId) {
    return <p className="text-sm text-[#4a5b68]">Connect a HubSpot portal to build reports.</p>;
  }

  return (
    <div className="space-y-6" data-testid="report-builder-panel">
      {error && (
        <p className="flex items-start gap-2 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          {error}
        </p>
      )}

      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        {/* ── Saved definitions ─────────────────────────────────────────── */}
        <div className="rounded-md border border-[#cccccc] bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Reports</p>
            <button
              type="button"
              onClick={() => setDraft(blankDefinition(sources[0].value))}
              className="inline-flex items-center gap-1 rounded-full bg-[#50e3c2] px-2 py-1 text-xs font-semibold text-[#323c44] hover:bg-[#57c1a9] uppercase tracking-[0.12em]"
              data-testid="new-report-definition"
            >
              <Plus className="h-3.5 w-3.5" /> New
            </button>
          </div>

          {definitions.length === 0 ? (
            <p className="py-6 text-center text-sm text-[#4a5b68]">
              No reports defined yet. Open the board pack to install the standard set.
            </p>
          ) : (
            <ul className="space-y-1">
              {definitions.map((definition) => (
                <li key={definition.key}>
                  <div
                    className={`flex items-center gap-2 rounded px-2 py-1.5 ${
                      draft?.key === definition.key ? "bg-[#efefef]" : "hover:bg-[#f5f5f5]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setDraft(definition)}
                      className="min-w-0 flex-1 text-left"
                    >
                      <span className="block truncate text-sm font-medium text-[#323c44]">
                        {definition.title}
                      </span>
                      <span className="block truncate text-[11px] text-[#7d8b96]">
                        {CHART_LABELS[definition.display?.chart] || definition.display?.chart}
                        {definition.hubspot_report_id ? ` — HubSpot ${definition.hubspot_report_id}` : ""}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(definition.key)}
                      className="rounded p-1 text-[#7d8b96] hover:bg-red-50 hover:text-red-600"
                      title={`Delete ${definition.title}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── Editor ────────────────────────────────────────────────────── */}
        {!draft ? (
          <div className="flex items-center justify-center rounded-md border border-dashed border-[#cccccc] bg-white p-12">
            <p className="text-sm text-[#4a5b68]">Pick a report to edit, or create a new one.</p>
          </div>
        ) : (
          <ReportDefinitionEditor
            portalId={portalId}
            definition={draft}
            sources={sources}
            onSaved={async (saved) => {
              await reloadDefinitions();
              setDraft(saved);
            }}
          />
        )}
      </div>
    </div>
  );
}
